import type { StepInProcess } from '@bliks-io/ui';
import { useProcessForView } from '@/lib/useProcessForView';

/**
 * One recorded step resolved out of its parent process, with everything the
 * product's step components want beside it.
 *
 * `useProcess`, not `useProcessStepRecording`: the recording endpoint returns a
 * deliberately MINIMAL recording, while those components need a
 * `ProcessStepRecordingDisplay` — which only the parent process carries. Pulling
 * a whole process to draw one step is the accepted cost; the query is cached and
 * shared with the detail page and with every other consumer on screen, so a
 * second step costs nothing.
 *
 * It lives here, beside `useProcessForView`, because finding the step is the
 * only thing it adds to that hook — the fetch, the readiness gate and the three
 * non-ready states are shared with the flow view.
 * `SourceRecordingPreviewDialog` hand-rolls the same resolution WITHOUT the
 * gate; adopting this hook there would also give it the suggestion set, which
 * is a behaviour change to a page that owns its own review.
 */
export function useStepInProcess(processId: string, stepRecordingId: string): StepInProcess {
  const state = useProcessForView(processId);
  if (state.status !== 'ready') return state;

  const step = state.data.process.processStepRecordings?.find(
    (recording) => recording._id === stepRecordingId
  );
  // A soft-deleted step needs no tombstone treatment: `show-process` fetches
  // recordings through the repository's default soft-delete exclusion, so one
  // can never reach this array — it arrives as `unavailable` instead.
  if (!step) return { status: 'unavailable' };

  return {
    status: 'ready',
    step,
    canViewSensitiveFields: state.data.capabilities.canViewSensitiveFields,
    canViewProcessInsights: state.data.capabilities.canViewProcessInsights,
    assigneeLookup: state.assigneeLookup,
    entitySuggestions: state.entitySuggestions
  };
}
